import { BadgeCheck } from "lucide-react";

// Official Signia Certified Partner logo (provided by user, hosted on Emergent assets)
const SIGNIA_CERT_LOGO = "https://customer-assets.emergentagent.com/job_styletto-staging/artifacts/g9ikua4j_SIgnia_Certified_Partner_Horizontal_Narrow%20%281%29%20%282%29.jpg";

/**
 * SigniaBadge — authorised-partner lockup.
 * Variants:
 *   - "compact"    → small inline lockup for header
 *   - "stacked"    → multi-line for hero/footer
 *   - "ribbon"     → horizontal banded badge with official cert logo
 *   - "wordmark"   → just the Signia wordmark
 *   - "logo"       → just the certified-partner logo image (auto-scaled)
 */
export default function SigniaBadge({ variant = "compact", className = "", inverse = false }) {
    const c = inverse ? "text-white" : "text-[#003E4D]";
    const sub = inverse ? "text-white/70" : "text-[#4a5568]";
    const dot = inverse ? "bg-white/30" : "bg-[#003E4D]/15";

    if (variant === "wordmark") {
        return (
            <span className={`signia-wordmark ${c} ${className}`} data-testid="signia-wordmark">
                Signia
            </span>
        );
    }

    if (variant === "logo") {
        return (
            <img
                src={SIGNIA_CERT_LOGO}
                alt="Signia Certified Partner"
                className={`h-12 md:h-14 w-auto object-contain ${className}`}
                data-testid="signia-cert-logo"
                loading="lazy"
            />
        );
    }

    if (variant === "stacked") {
        return (
            <div className={`flex flex-col items-start gap-2 ${className}`} data-testid="signia-badge-stacked">
                <img
                    src={SIGNIA_CERT_LOGO}
                    alt="Signia Certified Partner"
                    className="h-14 md:h-16 w-auto object-contain drop-shadow-[0_4px_18px_rgba(0,0,0,0.08)]"
                    loading="eager"
                />
            </div>
        );
    }

    if (variant === "ribbon") {
        // Ribbon = real certified-partner logo lockup, pill-boxed
        return (
            <div
                className={`inline-flex items-center gap-3 pl-2 pr-4 py-1.5 rounded-full border ${inverse ? "border-white/25 bg-white/[0.06]" : "border-[#003E4D]/12 bg-white shadow-[0_10px_24px_-16px_rgba(0,0,0,0.18)]"} ${className}`}
                data-testid="signia-badge-ribbon"
            >
                <img
                    src={SIGNIA_CERT_LOGO}
                    alt="Signia Certified Partner"
                    className="h-10 md:h-11 w-auto object-contain"
                    loading="eager"
                />
                <span className={`w-px h-6 ${inverse ? "bg-white/20" : "bg-[#003E4D]/15"}`} />
                <span className={`text-[10.5px] uppercase tracking-[0.22em] font-semibold ${sub}`}>
                    Powered by HearClear
                </span>
            </div>
        );
    }

    // default: compact — used in header
    return (
        <div className={`flex items-center gap-2.5 ${className}`} data-testid="signia-badge-compact">
            <img
                src={SIGNIA_CERT_LOGO}
                alt="Signia Certified Partner"
                className="h-8 w-auto object-contain"
                loading="lazy"
            />
            <span className={`w-px h-3.5 ${inverse ? "bg-white/25" : "bg-[#003E4D]/20"}`} />
            <span className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${sub}`}>
                Powered by HearClear
            </span>
            <BadgeCheck size={0} className="hidden" />
        </div>
    );
}
